import {
  formatStopDocument,
  getStoppingPointDocuments,
  hasCargaOperation,
  hasDescargaOperation,
  normalizeCanhotosList,
} from "../../view/frete-copy/action/action";

const DOCUMENT_ALIASES = {
  CTE: ["CTE", "CT-E", "CT_E"],
  MDFE: ["MDFE", "MDF-E", "MDF_E", "MANIFESTO"],
  NFE: ["NFE", "NF-E", "NF", "INVOICE", "NOTA FISCAL"],
  CIOT: ["CIOT"],
  VALE_PEDAGIO: ["VALE PEDAGIO", "VALE_PEDAGIO", "VALE-PEDAGIO", "VALE_PEDÁGIO"],
  BOLETO: ["BOLETO"],
};

function normalizeDocToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/\p{M}+/gu, "");
}

function collectRawDocuments(freightData) {
  const docs = [];
  const pushDoc = (item) => {
    if (item != null && item !== "") docs.push(item);
  };
  const pushBoleto = (item) => {
    if (item == null || item === "") return;
    if (typeof item === "object") {
      pushDoc({ ...item, type: item.type || "BOLETO" });
      return;
    }
    pushDoc({ type: "BOLETO", url: String(item), value: "Boleto" });
  };

  (freightData?.attachments || []).forEach(pushDoc);
  pushBoleto(freightData?.boletoAttachment);
  pushBoleto(freightData?.financial?.boletoAttachment);

  (freightData?.stopping_points || []).forEach((stop) => {
    (stop?.documents || []).forEach(pushDoc);
    (stop?.attachments || []).forEach(pushDoc);
  });

  return docs;
}

export function findFreightDocumentByActionType(freightData, actionType) {
  const aliases = DOCUMENT_ALIASES[actionType] || [actionType];
  const aliasSet = new Set(aliases.map(normalizeDocToken));
  const documents = collectRawDocuments(freightData);

  for (let index = 0; index < documents.length; index += 1) {
    const doc = documents[index];
    const formatted = formatStopDocument(doc, index);
    const typeToken = normalizeDocToken(
      typeof doc === "object"
        ? doc.type || doc.docType || doc.documentType || formatted.label
        : formatted.label,
    );
    const labelToken = normalizeDocToken(formatted.label);

    const matches = [...aliasSet].some(
      (alias) =>
        typeToken.includes(alias) ||
        labelToken.includes(alias) ||
        alias.includes(typeToken),
    );

    if (matches) {
      return formatted;
    }
  }

  return null;
}

export const FREIGHT_CONTROL_DOCUMENT_ACTIONS = [
  { key: "BOLETO", label: "Boleto" },
  { key: "CTE", label: "CT-e" },
  { key: "MDFE", label: "Manifesto" },
  { key: "CIOT", label: "CIOT" },
  { key: "VALE_PEDAGIO", label: "Vale Pedágio" },
  { key: "NFE", label: "NF" },
];

function resolveDocumentUrl(documentItem) {
  if (typeof documentItem === "string" || typeof documentItem === "number") {
    const url = String(documentItem).trim();
    return url.startsWith("http") ? url : "";
  }

  if (documentItem && typeof documentItem === "object") {
    return String(documentItem.url || documentItem.link || "").trim();
  }

  return "";
}

function formatDocumentEntry(rawDoc, index, sourcePrefix = "doc") {
  const formatted = formatStopDocument(rawDoc, index);
  const url = formatted.url || resolveDocumentUrl(rawDoc);

  return {
    id: `${sourcePrefix}-${index}-${formatted.label}-${formatted.value}`,
    label: formatted.label,
    name: formatted.value,
    url,
    canView: Boolean(url),
    canDownload: Boolean(url),
  };
}

function dedupeDocuments(documents) {
  const seen = new Set();
  return documents.filter((document) => {
    const key = document.url || `${document.label}-${document.name}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function getStoppingPointTitle(stop, index = 0) {
  const order = Number(stop?.stop_order) || index + 1;
  const parts = [`Parada ${order}`];

  if (hasCargaOperation(stop?.operation)) {
    parts.push("Carga");
  } else if (hasDescargaOperation(stop?.operation)) {
    parts.push("Descarga");
  }

  const name = String(stop?.name || "").trim();
  if (name) {
    parts.push(name);
  } else {
    const city = String(stop?.city || stop?.cidade || stop?.address?.city || "").trim();
    if (city) parts.push(city);
  }

  return parts.join(" · ");
}

function collectStopLevelDocuments(stop, stopIndex) {
  const documents = [];
  let documentIndex = 0;

  getStoppingPointDocuments(stop).forEach((documentItem) => {
    documents.push(
      formatDocumentEntry(documentItem, documentIndex, `stop-${stopIndex}-doc`),
    );
    documentIndex += 1;
  });

  (Array.isArray(stop?.attachments) ? stop.attachments : []).forEach(
    (attachment) => {
      if (!attachment?.url) return;
      documents.push(
        formatDocumentEntry(
          {
            ...attachment,
            type: attachment.type || attachment.docType || "ANEXO",
          },
          documentIndex,
          `stop-${stopIndex}-att`,
        ),
      );
      documentIndex += 1;
    },
  );

  normalizeCanhotosList(stop?.canhoto, stop?.id).forEach((canhoto) => {
    if (!canhoto?.url) return;
    documents.push(
      formatDocumentEntry(
        {
          ...canhoto,
          type: "CANHOTO",
          value: "Canhoto",
        },
        documentIndex,
        `stop-${stopIndex}-canhoto`,
      ),
    );
    documentIndex += 1;
  });

  return dedupeDocuments(documents);
}

export function buildFreightDocumentsByStoppingPoint(freightData) {
  const stops = Array.isArray(freightData?.stopping_points)
    ? [...freightData.stopping_points].sort(
        (left, right) =>
          (Number(left?.stop_order) || 0) - (Number(right?.stop_order) || 0),
      )
    : [];

  const sections = stops.map((stop, index) => ({
    id: stop.id || `stop-${index}`,
    title: getStoppingPointTitle(stop, index),
    documents: collectStopLevelDocuments(stop, index),
  }));

  const rootAttachments = Array.isArray(freightData?.attachments)
    ? freightData.attachments
    : [];
  const unattachedRoot = [];

  rootAttachments.forEach((attachment, index) => {
    if (!attachment?.url) return;

    const stopId = attachment.stoppingPointId;
    if (stopId) {
      const targetSection = sections.find((section) => section.id === stopId);
      if (targetSection) {
        targetSection.documents = dedupeDocuments([
          ...targetSection.documents,
          formatDocumentEntry(
            {
              ...attachment,
              type: attachment.type || attachment.docType || "ANEXO",
            },
            targetSection.documents.length,
            `root-${index}`,
          ),
        ]);
        return;
      }
    }

    unattachedRoot.push(
      formatDocumentEntry(
        {
          ...attachment,
          type: attachment.type || attachment.docType || "ANEXO",
        },
        index,
        `freight-${index}`,
      ),
    );
  });

  if (unattachedRoot.length > 0) {
    sections.unshift({
      id: "freight-general",
      title: "Documentos gerais do frete",
      documents: dedupeDocuments(unattachedRoot),
    });
  }

  return sections;
}

export function validateRequiredFreightDocumentsForBoletoSend(
  freightData,
  boletoAttachment,
) {
  const missingDocuments = [];

  FREIGHT_CONTROL_DOCUMENT_ACTIONS.filter(
    (action) => action.key !== "BOLETO",
  ).forEach((action) => {
    const document = findFreightDocumentByActionType(freightData, action.key);
    if (!document?.url) {
      missingDocuments.push(action.label);
    }
  });

  const stops = Array.isArray(freightData?.stopping_points)
    ? freightData.stopping_points
    : [];

  stops.forEach((stop, index) => {
    const stopDocuments = getStoppingPointDocuments(stop);
    const stopAttachments = (Array.isArray(stop?.attachments)
      ? stop.attachments
      : []
    ).filter((attachment) => attachment?.url);
    const stopCanhotos = normalizeCanhotosList(stop?.canhoto, stop?.id).filter(
      (canhoto) => canhoto?.url,
    );
    const hasStopFiles =
      stopDocuments.length > 0 ||
      stopAttachments.length > 0 ||
      stopCanhotos.length > 0;

    if (!hasStopFiles) {
      missingDocuments.push(
        `documentos no ponto de parada "${getStoppingPointTitle(stop, index)}"`,
      );
    }
  });

  const missingBoleto = !hasBoletoAttachmentValue(boletoAttachment);

  return {
    valid: missingDocuments.length === 0 && !missingBoleto,
    missingDocuments,
    missingBoleto,
  };
}

function hasBoletoAttachmentValue(boletoAttachment) {
  if (boletoAttachment == null || boletoAttachment === "") return false;
  if (typeof boletoAttachment === "string") {
    return boletoAttachment.trim().length > 0;
  }
  if (typeof boletoAttachment === "object") {
    return Boolean(
      String(boletoAttachment.url || "").trim() ||
        String(boletoAttachment.name || "").trim(),
    );
  }
  return false;
}

export function buildFreightInvoicePendingDocumentsMessage(validation) {
  const messages = [];

  if (validation?.missingDocuments?.length) {
    messages.push(
      `Documentos pendentes: ${validation.missingDocuments.join(", ")}.`,
    );
  }

  if (validation?.missingBoleto) {
    messages.push("O boleto ainda não foi anexado à fatura.");
  }

  return (
    messages.join(" ") ||
    "Não foi possível enviar o boleto. Verifique os anexos da fatura."
  );
}

function normalizeAttachmentUrl(value) {
  return String(value || "")
    .trim()
    .split("?")[0]
    .toLowerCase();
}

function sanitizeAttachmentFilename(value) {
  const sanitized = String(value || "arquivo")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_")
    .replace(/\s+/g, " ")
    .trim();

  return sanitized.slice(0, 180) || "arquivo";
}

function guessAttachmentContentType(filename) {
  const extension = String(filename || "")
    .split(".")
    .pop()
    .toLowerCase();

  const contentTypes = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    xml: "application/xml",
    txt: "text/plain",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };

  return contentTypes[extension] || "application/octet-stream";
}

function resolveFilenameFromUrl(url, fallbackName) {
  const fromUrl = String(url || "")
    .split("?")[0]
    .split("/")
    .pop();

  if (fromUrl) {
    try {
      return decodeURIComponent(fromUrl);
    } catch {
      return fromUrl;
    }
  }

  return fallbackName || "arquivo";
}

function ensureUniqueFilename(filename, usedNames) {
  const sanitized = sanitizeAttachmentFilename(filename);
  const lastDot = sanitized.lastIndexOf(".");
  const baseName = lastDot > 0 ? sanitized.slice(0, lastDot) : sanitized;
  const extension = lastDot > 0 ? sanitized.slice(lastDot) : "";

  let candidate = sanitized;
  let attempt = 1;

  while (usedNames.has(candidate.toLowerCase())) {
    attempt += 1;
    candidate = `${baseName} (${attempt})${extension}`;
  }

  usedNames.add(candidate.toLowerCase());
  return candidate;
}

function isBoletoDocumentEntry(document) {
  const token = normalizeDocToken(
    `${document?.label || ""} ${document?.name || ""}`,
  );
  return token.includes("BOLETO");
}

function resolveBoletoEmailAttachmentRef(boletoAttachment) {
  if (!boletoAttachment) return null;

  if (typeof boletoAttachment === "string") {
    const url = boletoAttachment.trim();
    if (!url || url === "local") return null;

    const filename = sanitizeAttachmentFilename(
      resolveFilenameFromUrl(url, "boleto.pdf"),
    );

    return {
      filename,
      url,
      contentType: guessAttachmentContentType(filename),
    };
  }

  if (typeof boletoAttachment !== "object") return null;

  const url = String(
    boletoAttachment.url || boletoAttachment.downloadUrl || "",
  ).trim();
  if (!url || url === "local") return null;

  const storagePath = String(
    boletoAttachment.storagePath ||
      boletoAttachment.path ||
      boletoAttachment.fileId ||
      "",
  ).trim();

  const filename = sanitizeAttachmentFilename(
    boletoAttachment.name ||
      boletoAttachment.originalName ||
      boletoAttachment.fileName ||
      resolveFilenameFromUrl(url, "boleto.pdf"),
  );

  return {
    filename,
    url,
    contentType: guessAttachmentContentType(filename),
    ...(storagePath ? { storagePath } : {}),
  };
}

function buildSectionDocumentFilename(document, sectionTitle) {
  const originalName = resolveFilenameFromUrl(
    document?.url,
    `${document?.label || "documento"}-${document?.name || "arquivo"}`,
  );
  const extensionMatch = originalName.match(/(\.[a-zA-Z0-9]+)$/);
  const extension = extensionMatch ? extensionMatch[1] : "";
  const label = String(document?.label || document?.name || "documento").trim();
  const sectionPrefix = String(sectionTitle || "").trim();

  if (sectionPrefix) {
    return sanitizeAttachmentFilename(
      `${sectionPrefix} - ${label}${extension}`,
    );
  }

  return sanitizeAttachmentFilename(`${label}${extension}` || originalName);
}

/**
 * Monta a lista de anexos do e-mail de boleto:
 * 1. Boleto (sempre primeiro)
 * 2. Demais documentos na mesma ordem/organização por ponto de parada
 *    usada na área de documentos.
 */
export function buildFreightBoletoEmailAttachmentRefs(
  freightData,
  boletoAttachment,
) {
  const attachmentRefs = [];
  const usedNames = new Set();
  const usedUrls = new Set();

  const boletoRef = resolveBoletoEmailAttachmentRef(boletoAttachment);
  if (boletoRef) {
    const filename = ensureUniqueFilename(boletoRef.filename, usedNames);
    attachmentRefs.push({
      ...boletoRef,
      filename,
    });
    usedUrls.add(normalizeAttachmentUrl(boletoRef.url));
  }

  const sections = buildFreightDocumentsByStoppingPoint(freightData || {});

  sections.forEach((section) => {
    (section.documents || []).forEach((document) => {
      const url = String(document?.url || "").trim();
      if (!url || url === "local") return;
      if (isBoletoDocumentEntry(document)) return;

      const normalizedUrl = normalizeAttachmentUrl(url);
      if (usedUrls.has(normalizedUrl)) return;

      const filename = ensureUniqueFilename(
        buildSectionDocumentFilename(document, section.title),
        usedNames,
      );

      attachmentRefs.push({
        filename,
        url,
        contentType: guessAttachmentContentType(filename),
      });
      usedUrls.add(normalizedUrl);
    });
  });

  return attachmentRefs;
}
